import { useEffect, useRef, useCallback, useState, type ReactNode } from 'react';
import type { GalleryImage, ViewerState } from '../../types/gallery';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import { ImageViewerSocial } from '../community/ImageViewerSocial';
import { formatGalleryDate, galleryImageDate } from './galleryImageMeta';
import './ImageViewer.css';

export interface ImageViewerProps {
  images: GalleryImage[];
  viewerState: ViewerState;
  aquariumName: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  /**
   * Editable details for the current image (AI analysis, tags, albums, note, journal
   * references). Without it the viewer shows the image's tags and note read-only.
   */
  renderDetails?: (image: GalleryImage) => ReactNode;
  /** Shows a "Delete photo" action; the caller confirms and deletes. */
  onDeleteRequest?: (image: GalleryImage) => void;
  /**
   * True while a dialog opened from the viewer (e.g. the delete confirmation) is on
   * top: the viewer then ignores Escape / Tab / arrow keys so only that dialog reacts.
   */
  suspended?: boolean;
  /**
   * Jumps to an image by index. Enables the filmstrip (small thumbnails under the
   * photo) and lets the slideshow start over at the first photo after the last.
   */
  onSelect?: (index: number) => void;
}

/** Time each photo stays on screen while the user-started slideshow plays. */
export const SLIDESHOW_INTERVAL_MS = 4000;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Arrow keys inside a form field move the caret, not the image. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function ImageViewer({
  images,
  viewerState,
  aquariumName,
  onClose,
  onPrev,
  onNext,
  renderDetails,
  onDeleteRequest,
  suspended = false,
  onSelect,
}: ImageViewerProps) {
  const { t, i18n } = useTranslation();
  const { isOpen, currentIndex } = viewerState;

  // Slideshow: only ever started by the user, always pausable, and not offered at
  // all under prefers-reduced-motion (WCAG 2.2.2 / 2.3.3).
  const [reducedMotion] = useState(prefersReducedMotion);
  const [playing, setPlaying] = useState(false);
  const filmstripRef = useRef<HTMLUListElement | null>(null);
  const currentThumbRef = useRef<HTMLButtonElement | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const scaleRef = useRef<number>(1);
  const panRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement | null>(null);

  const touchStartRef = useRef<{ x: number; y: number; distance: number } | null>(null);
  const isPinchingRef = useRef<boolean>(false);
  const animFrameRef = useRef<number | null>(null);

  const applyTransform = useCallback(() => {
    if (imageRef.current) {
      imageRef.current.style.transform = `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${scaleRef.current})`;
    }
  }, []);

  useEffect(() => {
    scaleRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    if (imageRef.current) {
      imageRef.current.style.transform = 'translate(0px, 0px) scale(1)';
    }
  }, [currentIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Stop the slideshow when the viewer closes or a dialog (delete confirmation) is on top.
  useEffect(() => {
    if (!isOpen || suspended) setPlaying(false);
  }, [isOpen, suspended]);

  // One timer per photo: a manual step restarts the interval. After the last photo
  // the slideshow starts over (with onSelect) or stops.
  useEffect(() => {
    if (!playing || !isOpen) return undefined;
    const timer = window.setTimeout(() => {
      if (currentIndex < images.length - 1) onNext();
      else if (onSelect && images.length > 1) onSelect(0);
      else setPlaying(false);
    }, SLIDESHOW_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [playing, isOpen, currentIndex, images.length, onNext, onSelect]);

  // Keep the current filmstrip thumbnail in view (horizontal scroll of the strip only,
  // so the page/panel never jumps). When the keyboard focus is in the strip, it
  // follows the current photo.
  useEffect(() => {
    if (!isOpen) return;
    const list = filmstripRef.current;
    const thumb = currentThumbRef.current;
    if (!list || !thumb) return;
    const left = thumb.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2;
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left: Math.max(0, left), behavior: reducedMotion ? 'auto' : 'smooth' });
    } else {
      list.scrollLeft = Math.max(0, left);
    }
    if (list.contains(document.activeElement) && document.activeElement !== thumb) thumb.focus();
  }, [isOpen, currentIndex, reducedMotion]);

  // Move focus into the viewer on open and back to the opening tile on close (WCAG 2.4.3).
  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    (closeRef.current ?? dialogRef.current)?.focus();
    return () => previouslyFocused?.focus?.();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // A dialog on top handles its own keys. For real key presses the browser runs
      // React's re-render between listeners, so by the time this window listener runs the
      // dialog may already have closed — it marks the Escape it handled as defaultPrevented.
      if (suspended || e.defaultPrevented) return;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        // Focus trap: Tab / Shift+Tab wrap inside the viewer.
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !dialog.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (isEditableTarget(e.target)) return;
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onPrev();
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, suspended, currentIndex, images.length, onClose, onPrev, onNext]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 1) {
      isPinchingRef.current = false;
      touchStartRef.current = {
        x: e.touches[0]!.clientX,
        y: e.touches[0]!.clientY,
        distance: 0,
      };
    } else if (e.touches.length === 2) {
      isPinchingRef.current = true;
      const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
      const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
      touchStartRef.current = {
        x: (e.touches[0]!.clientX + e.touches[1]!.clientX) / 2,
        y: (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2,
        distance: Math.sqrt(dx * dx + dy * dy),
      };
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!touchStartRef.current) return;

      if (e.touches.length === 2 && isPinchingRef.current) {
        const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
        const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
        const currentDistance = Math.sqrt(dx * dx + dy * dy);
        const delta = currentDistance / touchStartRef.current.distance;
        const newScale = Math.min(Math.max(scaleRef.current * delta, 0.5), 5);
        scaleRef.current = newScale;
        touchStartRef.current = {
          ...touchStartRef.current,
          distance: currentDistance,
        };
        if (animFrameRef.current !== null) {
          cancelAnimationFrame(animFrameRef.current);
        }
        animFrameRef.current = requestAnimationFrame(applyTransform);
      } else if (e.touches.length === 1 && !isPinchingRef.current) {
        const deltaX = e.touches[0]!.clientX - touchStartRef.current.x;
        const deltaY = e.touches[0]!.clientY - touchStartRef.current.y;
        panRef.current = {
          x: panRef.current.x + deltaX,
          y: panRef.current.y + deltaY,
        };
        touchStartRef.current = {
          ...touchStartRef.current,
          x: e.touches[0]!.clientX,
          y: e.touches[0]!.clientY,
        };
        if (animFrameRef.current !== null) {
          cancelAnimationFrame(animFrameRef.current);
        }
        animFrameRef.current = requestAnimationFrame(applyTransform);
      }
    },
    [applyTransform],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!touchStartRef.current || isPinchingRef.current) {
        isPinchingRef.current = false;
        touchStartRef.current = null;
        return;
      }

      const touch = e.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

      if (scaleRef.current <= 1 && Math.abs(deltaX) > 50 && deltaY < 80) {
        if (deltaX < 0 && currentIndex < images.length - 1) {
          onNext();
        } else if (deltaX > 0 && currentIndex > 0) {
          onPrev();
        }
      }

      touchStartRef.current = null;
    },
    [currentIndex, images.length, onNext, onPrev],
  );

  const handleZoomIn = useCallback(() => {
    scaleRef.current = Math.min(scaleRef.current * 1.25, 5);
    applyTransform();
  }, [applyTransform]);

  const handleZoomOut = useCallback(() => {
    scaleRef.current = Math.max(scaleRef.current / 1.25, 0.5);
    if (scaleRef.current <= 1) {
      panRef.current = { x: 0, y: 0 };
    }
    applyTransform();
  }, [applyTransform]);

  const handleResetZoom = useCallback(() => {
    scaleRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    applyTransform();
  }, [applyTransform]);

  if (!isOpen || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  if (!currentImage) {
    return null;
  }

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const imageSrc = currentImage.displayUrl ?? currentImage.thumbnailUrl ?? '';

  const photoDate = new Date(galleryImageDate(currentImage)).toLocaleDateString(i18n.language || undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const canPlay = !reducedMotion && images.length > 1;

  return (
    <div
      ref={dialogRef}
      className="image-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={t('gallery.viewer.label')}
      tabIndex={-1}
      data-testid="image-viewer"
    >
      <div
        className="image-viewer__stage"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pointer-only backdrop: clicking beside the photo closes the viewer.
            Keyboard users close with Escape or the close button. */}
        <div className="image-viewer__backdrop" aria-hidden="true" onClick={onClose} />
        <img
          ref={imageRef}
          src={imageSrc}
          alt={currentImage.originalFilename}
          className="image-viewer__img"
          draggable={false}
        />

        <Button
          ref={closeRef}
          className="image-viewer__control image-viewer__close"
          onClick={onClose}
          aria-label={t('gallery.viewer.close')}
          variant="ghost"
        >
          <span aria-hidden="true">✕</span>
        </Button>
        <span className="image-viewer__counter">
          {t('gallery.viewer.counter', { current: currentIndex + 1, total: images.length })}
        </span>

        {/* Visibility, share link, likes and comments (audit #11): a sheet over the photo. */}
        <ImageViewerSocial imageId={currentImage.id} />

        <Button
          className="image-viewer__control image-viewer__nav image-viewer__nav--prev"
          onClick={hasPrev ? onPrev : undefined}
          aria-label={t('gallery.viewer.previous')}
          variant="ghost"
          disabled={!hasPrev}
        >
          <span aria-hidden="true">‹</span>
        </Button>

        <Button
          className="image-viewer__control image-viewer__nav image-viewer__nav--next"
          onClick={hasNext ? onNext : undefined}
          aria-label={t('gallery.viewer.next')}
          variant="ghost"
          disabled={!hasNext}
        >
          <span aria-hidden="true">›</span>
        </Button>

        <div className="image-viewer__zoom-controls">
          {canPlay && (
            <Button
              className="image-viewer__control image-viewer__zoom-btn image-viewer__play"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? t('gallery.viewer.pauseSlideshow') : t('gallery.viewer.startSlideshow')}
              aria-pressed={playing}
              variant="ghost"
              data-testid="image-viewer-slideshow"
            >
              <span aria-hidden="true">{playing ? '❚❚' : '▶'}</span>
            </Button>
          )}
          <Button
            className="image-viewer__control image-viewer__zoom-btn"
            onClick={handleZoomIn}
            aria-label={t('gallery.viewer.zoomIn')}
            variant="ghost"
          >
            <span aria-hidden="true">+</span>
          </Button>
          <Button
            className="image-viewer__control image-viewer__zoom-btn"
            onClick={handleResetZoom}
            aria-label={t('gallery.viewer.resetZoom')}
            variant="ghost"
          >
            <span aria-hidden="true">⊙</span>
          </Button>
          <Button
            className="image-viewer__control image-viewer__zoom-btn"
            onClick={handleZoomOut}
            aria-label={t('gallery.viewer.zoomOut')}
            variant="ghost"
          >
            <span aria-hidden="true">−</span>
          </Button>
        </div>
      </div>

      {onSelect && images.length > 1 && (
        <nav className="image-viewer__filmstrip" aria-label={t('gallery.viewer.filmstripLabel')}>
          <ul ref={filmstripRef} className="image-viewer__filmstrip-list" data-testid="image-viewer-filmstrip">
            {images.map((image, index) => {
              const current = index === currentIndex;
              return (
                <li key={image.id} className="image-viewer__filmstrip-item">
                  <Button
                    ref={current ? currentThumbRef : undefined}
                    variant="ghost"
                    className={current ? 'image-viewer__thumb image-viewer__thumb--current' : 'image-viewer__thumb'}
                    aria-current={current ? 'true' : undefined}
                    aria-label={t('gallery.viewer.goTo', {
                      current: index + 1,
                      total: images.length,
                      date: formatGalleryDate(galleryImageDate(image), i18n.language),
                    })}
                    onClick={() => onSelect(index)}
                  >
                    <img
                      src={image.thumbnailUrl ?? image.displayUrl ?? undefined}
                      alt=""
                      className="image-viewer__thumb-img"
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  </Button>
                </li>
              );
            })}
          </ul>
        </nav>
      )}

      {/* Editing pauses the slideshow so an unsaved note never vanishes with the photo. */}
      <aside
        className="image-viewer__panel"
        aria-label={t('gallery.viewer.panelLabel')}
        onFocus={() => setPlaying(false)}
      >
        <div className="image-viewer__metadata">
          <Text weight="semibold">{aquariumName}</Text>
          <Text variant="caption">{photoDate}</Text>
          {currentImage.originalFilename && (
            <Text variant="caption" className="image-viewer__filename">{currentImage.originalFilename}</Text>
          )}
          {currentImage.hasAiAnalysis && (
            <Badge tone="ai" className="image-viewer__ai-badge">{t('gallery.aiBadgeLabel')}</Badge>
          )}
        </div>

        {renderDetails ? (
          renderDetails(currentImage)
        ) : (
          <>
            {currentImage.tags && currentImage.tags.length > 0 && (
              <ul className="image-viewer__tags">
                {currentImage.tags.map((tag) => (
                  <li key={tag}>
                    <Badge tone="neutral">{tag}</Badge>
                  </li>
                ))}
              </ul>
            )}
            {currentImage.notes && <Text className="image-viewer__notes">{currentImage.notes}</Text>}
          </>
        )}
        {onDeleteRequest && (
          <div className="image-viewer__danger-zone">
            <Button
              variant="danger"
              size="sm"
              onClick={() => onDeleteRequest(currentImage)}
              data-testid="image-viewer-delete"
            >
              {t('gallery.delete.action')}
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
}
